'use client';

import * as React from 'react';
import { createPortal } from 'react-dom';
import { useSession } from 'next-auth/react';

import { AudioTranscriptionHook } from '@audio-transcription/ui/AudioTranscriptionHook';
import { useSecurity } from '@core/ui/security/SecurityContext';
import { AudioPrivilegeIds } from '@audio-transcription/AudioTranscriptionFeatureModule';
import type { AudioTranscriptionContextValue } from '@audio-transcription/core/AudioTranscriptionTypes';
import { copyToClipboard, showManualCopyDialog } from '@audio-transcription/ui/ClipboardFeedbackService';
import { AudioTranscriptionOverlay } from '@audio-transcription/ui/AudioTranscriptionOverlay';

const AudioTranscriptionContext = React.createContext<AudioTranscriptionContextValue | null>(null);

export function useAudioTranscriptionFeature() {
  const ctx = React.useContext(AudioTranscriptionContext);
  if (!ctx) {
    throw new Error('useAudioTranscriptionFeature must be used within AudioTranscriptionProvider.');
  }
  return ctx;
}

type Feedback = { type: 'success' | 'error'; text: string } | null;

export function AudioTranscriptionProvider({ children }: { children: React.ReactNode }) {
  const { data: session, status } = useSession();
  const security = useSecurity();
  const isAuthenticated = status === 'authenticated';
  const isReadOnly = session?.user?.isReadOnly ?? false;
  // Vault-scoped check: tier is ceiling (Gate 1), role grants privilege (Gate 2)
  // SecurityProvider includes vault privileges filtered by tier entitlements
  const hasTranscribePrivilege = security.hasPrivilege(AudioPrivilegeIds.transcribe, 'vault');
  const isAuthorized = isAuthenticated && !isReadOnly && hasTranscribePrivilege;

  const timeoutRef = React.useRef<number | null>(null);
  const [overlayForced, setOverlayForced] = React.useState(false);
  const [feedback, setFeedback] = React.useState<Feedback>(null);
  const pendingOnTranscribedRef = React.useRef<((text: string) => void) | undefined>(undefined);

  const handleTranscriptionComplete = React.useCallback(
    async (text: string) => {
      // Preserve currently focused element and selection (so inputs don't lose caret)
      const prevActive = (typeof document !== 'undefined' ? (document.activeElement as HTMLElement | null) : null);
      const prevSelection = (() => {
        if (prevActive instanceof HTMLInputElement || prevActive instanceof HTMLTextAreaElement) {
          return {
            start: prevActive.selectionStart ?? null,
            end: prevActive.selectionEnd ?? null,
          } as { start: number | null; end: number | null };
        }
        return null;
      })();

      const copied = await copyToClipboard(text);
      pendingOnTranscribedRef.current?.(text);
      pendingOnTranscribedRef.current = undefined;

      if (copied) {
        setFeedback({ type: 'success', text: 'Transcription copied to clipboard' });
      } else {
        setFeedback({ type: 'error', text: 'Copied text unavailable. Transcription shown below.' });
      }
      setOverlayForced(true);

      if (!copied) {
        showManualCopyDialog(text);
      }

      // Attempt to restore focus and selection to the previously focused element
      try {
        if (prevActive && typeof (prevActive as any).focus === 'function') {
          (prevActive as any).focus();
          if (prevSelection && (prevActive instanceof HTMLInputElement || prevActive instanceof HTMLTextAreaElement)) {
            const s = prevSelection.start ?? prevActive.value.length;
            const e = prevSelection.end ?? s;
            try {
              prevActive.setSelectionRange(s, e);
            } catch {}
          }
        }
      } catch {}

      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = window.setTimeout(() => {
        setFeedback(null);
        setOverlayForced(false);
      }, 2200);
    },
    []
  );

  const transcription = AudioTranscriptionHook.useAudioTranscription({
    language: undefined,
    onError: (message) => {
      setOverlayForced(true);
      setFeedback({ type: 'error', text: message });
    },
    onTranscribed: (text) => {
      void handleTranscriptionComplete(text);
    },
    source: 'global',
  });

  const { state, seconds } = transcription;

  const clearFeedback = React.useCallback(() => {
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setFeedback(null);
    if (transcription.state === 'idle') {
      setOverlayForced(false);
    }
  }, [transcription.state]);

  React.useEffect(() => () => {
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
    }
  }, []);

  const start = React.useCallback(
    async (options?: { onTranscribed?: (text: string) => void }) => {
      if (!isAuthorized) return;
      pendingOnTranscribedRef.current = options?.onTranscribed;
      setOverlayForced(true);
      await transcription.startRecording();
    },
    [isAuthorized, transcription]
  );

  const cancel = React.useCallback(() => {
    transcription.cancelRecording();
    pendingOnTranscribedRef.current = undefined;
    setOverlayForced(false);
    setFeedback(null);
  }, [transcription]);

  const accept = React.useCallback(() => {
    transcription.acceptRecording({
      onTranscribed: (text) => {
        pendingOnTranscribedRef.current?.(text);
        pendingOnTranscribedRef.current = undefined;
      },
    });
  }, [transcription]);

  const toggle = React.useCallback(() => {
    if (!isAuthorized) return;
    if (transcription.state === 'idle') {
      void start();
      return;
    }
    if (transcription.state === 'recording') {
      accept();
    }
  }, [accept, isAuthorized, start, transcription.state]);

  React.useEffect(() => {
    if (!isAuthorized) return;
    const onHotkey = () => {
      toggle();
    };
    window.addEventListener('hotkey:audio.toggle', onHotkey);
    return () => {
      window.removeEventListener('hotkey:audio.toggle', onHotkey);
    };
  }, [isAuthorized, toggle]);

  React.useEffect(() => {
    if (!isAuthorized || transcription.state !== 'recording') return;
    const handler = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      cancel();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [cancel, isAuthorized, transcription.state]);

  React.useEffect(() => {
    if (!transcription.error) return;
    setOverlayForced(true);
    setFeedback({ type: 'error', text: transcription.error });
  }, [transcription.error]);

  const showOverlay = overlayForced || transcription.state !== 'idle';

  const overlayNode =
    showOverlay && typeof document !== 'undefined'
      ? createPortal(
          <AudioTranscriptionOverlay
            state={transcription.state}
            seconds={seconds}
            analyser={transcription.analyser}
            dataArray={transcription.dataArray}
            bufferLength={transcription.bufferLength}
            onCancel={cancel}
            onAccept={accept}
            feedback={feedback}
            onDismissFeedback={clearFeedback}
          />,
          document.body
        )
      : null;

  const contextValue = React.useMemo<AudioTranscriptionContextValue>(
    () => ({
      isAvailable: isAuthorized,
      state,
      seconds,
      start,
      cancel,
      accept,
      toggle,
    }),
    [accept, cancel, isAuthorized, seconds, start, state, toggle]
  );

  return (
    <AudioTranscriptionContext.Provider value={contextValue}>
      {children}
      {overlayNode}
    </AudioTranscriptionContext.Provider>
  );
}
export function AudioTranscriptionDebugNotice() {
  const { isAvailable } = useAudioTranscriptionFeature();
  return (
    <div className='hidden text-xs text-gray-400'>
      {isAvailable ? 'Audio transcription enabled for this account.' : 'Audio transcription limited to authorized users.'}
    </div>
  );
}
